import { useState, useMemo } from 'react';
import SEO from '../../components/SEO';
import AdUnit from '../../components/AdUnit';

function SipCalculator() {
  var [monthly, setMonthly] = useState(10000);
  var [rate, setRate] = useState(12);
  var [years, setYears] = useState(10);
  var [lumpsum, setLumpsum] = useState(0);
  var [stepUp, setStepUp] = useState(0);

  var result = useMemo(function () {
    var r = rate / 100 / 12;
    var n = years * 12;
    var totalInvested = 0;
    var futureValue = lumpsum * Math.pow(1 + r, n);
    totalInvested += lumpsum;

    if (stepUp > 0) {
      // Step-up SIP: increase monthly amount by stepUp% every year
      var fv = lumpsum * Math.pow(1 + r, n);
      for (var y = 0; y < years; y++) {
        var currentMonthly = monthly * Math.pow(1 + stepUp / 100, y);
        for (var m = 0; m < 12; m++) {
          var monthsLeft = n - (y * 12 + m);
          fv += currentMonthly * Math.pow(1 + r, monthsLeft);
          totalInvested += currentMonthly;
        }
      }
      futureValue = fv;
    } else {
      // Standard SIP formula
      if (r > 0) {
        futureValue += monthly * ((Math.pow(1 + r, n) - 1) / r) * (1 + r);
      } else {
        futureValue += monthly * n;
      }
      totalInvested += monthly * n;
    }

    var wealth = futureValue - totalInvested;
    return {
      invested: totalInvested,
      returns: wealth,
      total: futureValue,
      multiplier: totalInvested > 0 ? (futureValue / totalInvested).toFixed(1) : '0',
    };
  }, [monthly, rate, years, lumpsum, stepUp]);

  var fmt = function (n) {
    var abs = Math.abs(Math.round(n));
    var sign = n < 0 ? '-' : '';
    if (abs >= 10000000) return sign + '₹' + (abs / 10000000).toFixed(2) + ' Cr';
    if (abs >= 100000) return sign + '₹' + (abs / 100000).toFixed(2) + ' L';
    return sign + '₹' + abs.toLocaleString('en-IN');
  };

  return (
    <div className="page-prettifier">
      <SEO title="SIP Calculator - Mutual Fund Returns Calculator India" description="Free SIP calculator for Indian mutual funds. Calculate future value of monthly investments with step-up SIP, lumpsum, and expected returns." path="/sip-calculator" />
      <div className="container">
        <div className="tool-header">
          <h1>SIP Calculator (Systematic Investment Plan)</h1>
          <p>Calculate the future value of your monthly SIP investments with step-up and lumpsum options.</p>
        </div>
        <AdUnit />
        <div className="calc-layout">
          <div className="calc-inputs">
            <div className="form-group">
              <label htmlFor="sip-monthly">Monthly SIP Amount (₹)</label>
              <input id="sip-monthly" type="number" min="100" value={monthly} onChange={function (e) { setMonthly(parseFloat(e.target.value) || 0); }} className="color-input" />
            </div>
            <div className="form-group">
              <label htmlFor="sip-rate">Expected Annual Return (%)</label>
              <input id="sip-rate" type="number" min="0" max="50" step="0.5" value={rate} onChange={function (e) { setRate(parseFloat(e.target.value) || 0); }} className="color-input" />
            </div>
            <div className="form-group">
              <label htmlFor="sip-years">Investment Period (years)</label>
              <input id="sip-years" type="number" min="1" max="50" value={years} onChange={function (e) { setYears(parseInt(e.target.value) || 1); }} className="color-input" />
            </div>
            <div className="form-group">
              <label htmlFor="sip-lumpsum">Initial Lumpsum (₹, optional)</label>
              <input id="sip-lumpsum" type="number" min="0" value={lumpsum} onChange={function (e) { setLumpsum(parseFloat(e.target.value) || 0); }} className="color-input" />
            </div>
            <div className="form-group">
              <label htmlFor="sip-stepup">Annual Step-Up (%, optional)</label>
              <input id="sip-stepup" type="number" min="0" max="50" step="1" value={stepUp} onChange={function (e) { setStepUp(parseFloat(e.target.value) || 0); }} className="color-input" />
            </div>
          </div>
          <div className="calc-results">
            <div className="stat-card" style={{ flex: 'none', width: '100%' }}>
              <div className="stat-number" style={{ color: 'var(--accent)' }}>{fmt(result.total)}</div>
              <div className="stat-label">Total Future Value</div>
            </div>
            <div className="stats-grid" style={{ width: '100%' }}>
              <div className="stat-card"><div className="stat-number">{fmt(result.invested)}</div><div className="stat-label">Total Invested</div></div>
              <div className="stat-card"><div className="stat-number" style={{ color: 'var(--accent)' }}>{fmt(result.returns)}</div><div className="stat-label">Wealth Gained</div></div>
              <div className="stat-card"><div className="stat-number">{result.multiplier}x</div><div className="stat-label">Money Multiplier</div></div>
            </div>
            <div style={{ marginTop: '1rem', width: '100%', height: '24px', borderRadius: '12px', overflow: 'hidden', display: 'flex', border: '1px solid var(--border)' }}>
              <div style={{ width: (result.total > 0 ? (result.invested / result.total * 100) : 50) + '%', background: '#6b7280', transition: 'width 0.3s' }} title="Invested" />
              <div style={{ flex: 1, background: 'var(--accent)', transition: 'width 0.3s' }} title="Returns" />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.25rem', width: '100%' }}>
              <span>Invested</span><span>Returns</span>
            </div>
          </div>
        </div>
        <section className="tool-guide">
          <h2>What is SIP?</h2>
          <p>A Systematic Investment Plan (SIP) lets you invest a fixed amount regularly in mutual funds. It leverages rupee cost averaging and the power of compounding to grow your wealth over time. Even small monthly investments can build significant wealth over 10-20 years.</p>
          <h2>Step-Up SIP</h2>
          <p>A step-up SIP increases your monthly investment by a fixed percentage every year (e.g., 10%). This aligns with salary increments and dramatically boosts your corpus. A 10% annual step-up on ₹10,000/month SIP can nearly double your final corpus compared to a flat SIP.</p>
          <h2>SIP Investment Tips</h2>
          <ul>
            <li>Start early — even ₹5,000/month at 12% for 25 years grows to over ₹1 crore.</li>
            <li>Use step-up SIP to increase investments as your income grows.</li>
            <li>Stay invested through market dips — SIP benefits from rupee cost averaging.</li>
            <li>Choose direct plans over regular plans to save 0.5-1% in expense ratio.</li>
            <li>ELSS funds offer tax benefits under Section 80C with a 3-year lock-in.</li>
          </ul>
        </section>
      </div>
    </div>
  );
}

export default SipCalculator;
